'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import LanguageSwitcher from '@/components/language/LanguageSwitcher';
import { useLanguage } from '@/components/language/LanguageProvider';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { dictionary } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileOpen]);

  const navLinks = [
    { label: dictionary.header.nav.portfolio, href: '#portfolio' },
    { label: dictionary.header.nav.about, href: '#about' },
    { label: dictionary.header.nav.styles, href: '#styles' },
  ];

  const handleNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    closeMobileMenu = false
  ) => {
    if (!href.startsWith('#')) return;

    const target = document.querySelector(href);
    if (!target) return;

    event.preventDefault();
    const headerOffset = 110;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
    window.history.replaceState(null, '', href);

    if (closeMobileMenu) {
      setMobileOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? 'bg-[#0D0D0D]/92 backdrop-blur-2xl border-b border-white/[0.06] shadow-[0_1px_0_rgba(200,169,110,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full max-w-[1460px] mx-auto px-4 sm:px-6 lg:px-10 py-4 lg:py-5 grid grid-cols-[auto_1fr_auto] items-center gap-3">
        {/* Logo / Wordmark */}
        <Link href="/" className="flex items-center gap-3.5 group">
          <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-primary/25 via-primary/10 to-transparent border border-primary/35 flex items-center justify-center transition-all duration-300 group-hover:border-primary/60 group-hover:shadow-[0_0_18px_rgba(200,169,110,0.24)]">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 1L10 6H14L11 9.5L12 14L8 11.5L4 14L5 9.5L2 6H6L8 1Z" fill="#C8A96E" />
            </svg>
          </div>
          <div className="flex flex-col leading-none">
            <span
              className="text-foreground font-semibold text-[1.02rem] tracking-[0.01em] group-hover:text-primary transition-colors duration-300"
              style={{ fontFamily: 'var(--font-fraunces)' }}
            >
              juga<span className="text-primary">_</span>tattoo
            </span>
            <span className="hidden sm:block text-[9px] tracking-[0.16em] text-muted-foreground/70 mt-1">
              {dictionary.hero.location}
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center justify-center gap-2">
          {navLinks?.map((link) => (
            <a
              key={link?.href}
              href={link?.href}
              onClick={(event) => handleNavClick(event, link.href)}
              className="relative text-muted-foreground hover:text-foreground text-sm font-medium tracking-wide transition-colors duration-300 px-5 py-2.5 rounded-lg hover:bg-white/[0.04] group"
            >
              {link?.label}
              <span className="absolute bottom-1 left-5 right-5 h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center justify-end gap-3">
          <LanguageSwitcher />
          <a
            href="https://www.instagram.com/juga_tattoo/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300"
            aria-label="Instagram @juga_tattoo"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
            </svg>
          </a>
          <a
            href="#booking"
            onClick={(event) => handleNavClick(event, '#booking')}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-bold hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(200,169,110,0.35)] active:translate-y-0"
          >
            {dictionary.header.book}
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden justify-self-end text-foreground p-2 rounded-lg hover:bg-white/[0.06] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? dictionary.header.closeMenu : dictionary.header.openMenu}
        >
          {mobileOpen ? <Icon name="XMarkIcon" size={22} /> : <Icon name="Bars3Icon" size={22} />}
        </button>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0D0D0D]/97 backdrop-blur-2xl border-b border-white/[0.08] px-4 sm:px-6 py-5 flex flex-col gap-1">
          <div className="px-3 pb-3">
            <LanguageSwitcher compact />
          </div>
          {navLinks?.map((link) => (
            <a
              key={link?.href}
              href={link?.href}
              onClick={(event) => handleNavClick(event, link.href, true)}
              className="text-foreground/80 font-medium text-base py-3 px-3 rounded-xl hover:bg-white/[0.05] hover:text-primary transition-all duration-200"
            >
              {link?.label}
            </a>
          ))}
          <div className="pt-3 mt-2 border-t border-white/[0.06]">
            <a
              href="#booking"
              onClick={(event) => handleNavClick(event, '#booking', true)}
              className="block bg-primary text-primary-foreground text-center px-6 py-3.5 rounded-full font-bold text-sm hover:bg-primary/90 transition-colors"
            >
              {dictionary.header.book}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
